import { createRoot } from 'react-dom/client';
// Inter 3.19, the version Figma ships (matches Figma's text metrics)
import 'inter-ui/inter.css';
import './styles.css';
import { app, installBrowserMeasurer } from './app.ts';
import { installKeyboard } from './commands.ts';
import { App } from './ui/App.tsx';
import { SignIn } from './ui/Account.tsx';
import { installFeatureFonts } from './fontFeatures.ts';
import { installPluginHost } from './plugins.ts';
import { applyTheme } from './theme.ts';
import { loadLibraryFonts } from './customFonts.ts';
import { discoverLocalFonts } from './localFonts.ts';
import { installShaping } from './shaping.ts';

applyTheme();

const timeout = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function boot() {
  // Everything a visit needs starts at once (the page preloads the files too): who is signed in,
  // the fonts, the text shaper, and as soon as the account is known, the file itself. Layout waits
  // for the real fonts (see `ready`), the download of the file doesn't.
  const me = fetch('/api/me')
    .then((r) => r.json())
    .catch(() => null);
  // the weights the interface and most text use; the rest load once the file shows (so they don't
  // share the connection with what it waits for) and text redraws when they're in
  const uiFonts = Promise.race([Promise.all([400, 500, 600, 700].map((w) => document.fonts.load(`${w} 16px Inter`)).concat(document.fonts.load('italic 400 16px Inter'))), timeout(3000)]).then(() => performance.mark('vellum:fonts'));
  installBrowserMeasurer();
  installFeatureFonts(() => app.fontsReady());
  // HarfBuzz shapes and draws all design text (same as the server); the canvas measurer above is the fallback
  const shaping = installShaping().then(() => performance.mark('vellum:shaping'));
  // text is measured once these are in: until then, what the server sends waits
  const ready = Promise.all([uiFonts, shaping]);
  void ready.then(() => {
    void Promise.all([100, 200, 300, 800, 900].map((w) => document.fonts.load(`${w} 16px Inter`)).concat(document.fonts.load('italic 700 16px Inter'))).then(() => app.fontsReady());
    void loadLibraryFonts();
    // fonts installed on this computer, when permission was given before (the desktop app always has it)
    void discoverLocalFonts(false);
  });
  const root = createRoot(document.getElementById('root')!);
  // servers with accounts need a signed-in person before the file connects
  const account = await me;
  if (account) app.account = account;
  performance.mark('vellum:account');
  if (app.account.authEnabled && !app.account.user) {
    root.render(<SignIn onDone={() => location.reload()} sso={(app.account as { sso?: { name: string; only: boolean } | null }).sso} />);
    return;
  }
  if (app.account.user && app.account.authEnabled) app.userName = app.account.user.name;
  installKeyboard();
  installPluginHost();
  const params = new URLSearchParams(location.search);
  let fileId = params.get('file');
  // no file in the address: the one edited last, with All files open (a new file only on a first
  // visit, not every time someone opens the bare URL)
  let recent = false;
  if (!fileId) {
    try {
      const files = (await (await fetch('/api/files')).json()) as { id: string; updatedAt: number }[];
      fileId = Array.isArray(files) && files.length ? files.reduce((a, b) => (b.updatedAt > a.updatedAt ? b : a)).id : null;
      recent = !!fileId;
    } catch {
      /* offline: a new file */
    }
  }
  app.connect(fileId, ready);
  if (recent) app.setUI({ homeOpen: true });
  // edits that haven't reached the server yet (offline, or still sending) would be lost with the tab
  window.addEventListener('beforeunload', (e) => {
    if (!app.sync?.busy) return;
    e.preventDefault();
    e.returnValue = '';
  });
  // links from editors and tools can open straight into Dev Mode
  if (params.get('mode') === 'dev') app.setUI({ devMode: true });
  root.render(<App />);
  // with WebGPU, this tab also draws the open file's shaders for agent screenshots and exports
  if (app.shaders.available && !app.readOnly) void import('./shaders/host.ts').then((m) => m.runShaderHost({ file: () => app.fileId, runtime: app.shaders }));
}

// the desktop app's hidden shader host: no editor, just shader jobs for headless renders
const hostParams = new URLSearchParams(location.search);
if (hostParams.has('shaderHost')) void import('./shaders/host.ts').then((m) => m.runShaderHost({ token: location.hash.slice(1) }));
else void boot();

// installable app: the shell works offline and repeat visits load from disk (production builds
// only, so dev reloads stay fresh). It starts once the page is idle, so its downloads don't compete
// with the first one.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  const register = () => void navigator.serviceWorker.register('/sw.js').catch(() => {});
  const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(register, { timeout: 10000 }) : setTimeout(register, 3000));
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
}

// a tab left open across a deploy asks for code the server no longer has: load the new version
// (once, and not while edits are still on their way)
window.addEventListener('vite:preloadError', (e) => {
  try {
    if (app.sync?.busy || Date.now() - Number(sessionStorage.getItem('vellum:reloaded') ?? 0) < 30000) return;
    sessionStorage.setItem('vellum:reloaded', String(Date.now()));
  } catch {
    return;
  }
  e.preventDefault();
  location.reload();
});
